import type { ReactNode } from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtCurrencyCobranza } from '@/lib/portal-cobranza/formato';
import {
  CLASE_TONO_MONTO, prefijoLecturaMonto, sentidoDeMonto, type EstiloMonto, type SentidoMonto,
} from '@/lib/montoSentido';

/**
 * Monto de dinero con su sentido, como en una app financiera: lo que entra o es a favor en verde
 * con flecha hacia arriba (`TrendingUp`), lo que se debe en rojo con flecha hacia abajo
 * (`TrendingDown`). Neutro sin flecha ni color; 0 sin flecha y en gris. La regla vive en
 * `lib/montoSentido` (pura, con test).
 *
 * - `size`: `sm` tablas (ícono 12px), `md` filas destacadas (14px), `lg` KPIs (16px).
 * - La flecha va ANTES del número: en una columna alineada a la derecha los montos siguen
 *   cuadrando por el último dígito y la fila no crece (inline-flex, sin salto de línea).
 * - El ícono es decorativo (`aria-hidden`); el lector de pantalla oye "Ingreso de $X" o
 *   "Adeudo de $X" con un prefijo `sr-only` (un `aria-label` sobre un `span` genérico no se
 *   anuncia en todos los lectores).
 * - `colorear={false}` deja el color del contenedor (por ejemplo, un footer ya en negritas) y
 *   solo agrega la flecha.
 * - `texto` reemplaza el formato (montos compactos, por ejemplo); sin monto se lee "Sin monto".
 */

export type TamanoMonto = 'sm' | 'md' | 'lg';

const ICONO: Record<TamanoMonto, string> = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

export interface MontoTendenciaProps {
  monto: number | null | undefined;
  sentido: SentidoMonto;
  size?: TamanoMonto;
  /** Texto a mostrar en lugar de `fmtCurrencyCobranza(monto)` (ya formateado). */
  texto?: ReactNode;
  /**
   * Desde cuántos centavos se marca (default 1). Cobranza avisa desde $1 (`UMBRAL_AVISO_CENTAVOS`):
   * por debajo el monto queda neutro, sin flecha.
   */
  umbralCentavos?: number;
  /** `false` = solo la flecha, sin cambiar el color del texto. */
  colorear?: boolean;
  className?: string;
}

/** Ícono de la tendencia (o `null`). Exportado para quien ya pinta el número por su cuenta. */
export function FlechaMonto({ estilo, size = 'sm', className }: { estilo: EstiloMonto; size?: TamanoMonto; className?: string }) {
  if (!estilo.flecha) return null;
  const Icono = estilo.flecha === 'arriba' ? TrendingUp : TrendingDown;
  return <Icono aria-hidden className={cn('shrink-0', ICONO[size], className)} strokeWidth={2.25} />;
}

export function MontoTendencia({
  monto, sentido, size = 'sm', texto, umbralCentavos, colorear = true, className,
}: MontoTendenciaProps) {
  const estilo = sentidoDeMonto(monto, sentido, umbralCentavos);
  const prefijo = prefijoLecturaMonto(estilo);
  const visible = texto ?? fmtCurrencyCobranza(monto);
  return (
    <span
      className={cn(
        'inline-flex items-center justify-end gap-1 whitespace-nowrap tabular-nums align-middle',
        colorear && CLASE_TONO_MONTO[estilo.tono],
        !colorear && estilo.tono === 'vacio' && CLASE_TONO_MONTO.vacio,
        estilo.tono === 'vacio' && 'font-normal',
        className,
      )}
    >
      <FlechaMonto estilo={estilo} size={size} />
      {prefijo && <span className="sr-only">{prefijo} </span>}
      <span>{visible}</span>
    </span>
  );
}
